import React, { useEffect, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";

import { ZodProvider } from "@autoform/zod";
import {
  useNavigation,
  useNotification,
  useParsed,
  useWarnAboutChange,
} from "@refinedev/core";
import { useDocumentTitle } from "@refinedev/react-router";

import { BottomActionBar } from "@/components/bottom-action-bar";
import { DeleteButton } from "@/components/refine-ui/buttons/delete";
import { AutoForm } from "@/components/ui/autoform";
import { injectCustomAutoFormComponents } from "@/components/ui/autoform/components/custom";
import { Button } from "@/components/ui/button";
import { useLoadingOverlay } from "@/contexts/loading-context";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { getDirtyValues, isFormDirty } from "@/lib/utils";
import { parseRead } from "@/lib/zod/read-schema";
import { PerformanceCastField } from "@/pages/performances/components/fields/performance-cast-field";
import {
  startDateForPicker,
  startDateForSubmit,
} from "@/pages/performances/lib/performance-start-date";
import {
  PerformanceEditFormType,
  type PerformanceOptionalFieldPresence,
  buildPerformanceEditSchema,
  performanceFieldPresence,
} from "@/pages/performances/schemas/performance.edit.form";
import { useTheaterRepository } from "@/pages/theater/hooks/use-theater-repository";
import { useVenueRepository } from "@/pages/venues/hooks/use-venue-repository";

const NO_OPTIONAL_FIELDS: PerformanceOptionalFieldPresence = {
  ticketUrl: false,
  pricing: false,
  screen: false,
};

/**
 * Edit one showing. The form models only what `UpdatePerformanceCommand` accepts; play and
 * venue are denormalized copies the backend re-derives.
 *
 * The venue document is read for ONE field: `timezone`. `PerformanceVenueDbDto` carries no
 * timezone (spec §3.6) and the DateTimePicker holds browser-local wall clock, so without it
 * a curator outside the venue's zone would silently shift every curtain time. No timezone
 * → a visible warning and browser-local semantics, never a silent guess.
 */
export const PerformancesEdit = () => {
  const { getPerformance, updatePerformance } = useTheaterRepository();
  const { get: getVenue } = useVenueRepository();
  const { id } = useParsed();
  const [values, setValues] = React.useState<PerformanceEditFormType>();
  const [present, setPresent] =
    React.useState<PerformanceOptionalFieldPresence>(NO_OPTIONAL_FIELDS);
  const [timezone, setTimezone] = React.useState<string | undefined>();
  const [timezoneKnown, setTimezoneKnown] = React.useState(true);
  const { open } = useNotification();
  const { setLoading } = useLoadingOverlay();
  const { setWarnWhen, warnWhen } = useWarnAboutChange();
  const { list } = useNavigation();
  const setTitle = useDocumentTitle();

  const schemaProvider = useMemo(
    () => new ZodProvider(buildPerformanceEditSchema(present)),
    [present.ticketUrl, present.pricing, present.screen],
  );

  useEffect(() => {
    if (!id) {
      open?.({
        type: "error",
        message: "Something went wrong. This shouldn't be happening",
      });
      return;
    }
    setLoading(true);
    getPerformance(id.toString())
      .then(async (response) => {
        const raw = response as Record<string, any>;
        const detected = performanceFieldPresence(raw);
        const venueId: string | undefined = raw?.venueId ?? raw?.venue?.id;

        let zone: string | undefined;
        if (venueId) {
          try {
            const venue = await getVenue(venueId);
            zone = venue?.timezone || undefined;
          } catch (error) {
            console.error("venue read for timezone failed", error);
          }
        }

        const data = parseRead(
          buildPerformanceEditSchema(detected),
          { ...raw, startDate: startDateForPicker(raw?.startDate, zone) },
          "performance",
        ) as PerformanceEditFormType;

        setPresent(detected);
        setTimezone(zone);
        setTimezoneKnown(!!zone);
        setValues(data);
        setTitle(
          "Edit " + (raw?.play?.title ?? raw?._playTitle ?? "performance"),
        );
      })
      .catch((error) => {
        console.error(error);
        open?.({
          type: "error",
          message: "Failed to get performance",
          description: toHumanErrorMessage(error),
        });
      })
      .finally(() => setLoading(false));
  }, [id]);

  const initializeForm = (
    form: UseFormReturn<PerformanceEditFormType, any, PerformanceEditFormType>,
  ) => {
    form.watch((value) => {
      setWarnWhen(
        form.formState.isDirty &&
          isFormDirty(
            form.formState.dirtyFields as Record<string, unknown>,
            value as Record<string, unknown>,
            form.formState.defaultValues as Record<string, unknown>,
          ),
      );
    });
  };

  const onSubmit = async (
    allValues: PerformanceEditFormType,
    form: UseFormReturn<PerformanceEditFormType, any, PerformanceEditFormType>,
  ) => {
    const dirty = getDirtyValues(
      form.formState.dirtyFields as Record<string, unknown>,
      allValues,
    ) as Record<string, unknown>;
    if (dirty.startDate !== undefined) {
      dirty.startDate = startDateForSubmit(dirty.startDate as string, timezone);
    }
    setWarnWhen(false);
    await updatePerformance({ id: allValues.id, ...dirty });
  };

  return (
    <div className="flex flex-row">
      <div className="flex w-full flex-col space-y-4 p-8">
        {!timezoneKnown && (
          <div
            role="alert"
            className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm"
          >
            This venue has no timezone, or could not be read, so the start time
            is shown and saved in your own timezone. Set the venue timezone
            before changing it.
          </div>
        )}
        {values && (
          <AutoForm
            schema={schemaProvider}
            defaultValues={values}
            onSubmit={onSubmit}
            onFormInit={initializeForm}
            {...injectCustomAutoFormComponents({
              formComponents: { performanceCast: PerformanceCastField },
            })}
          >
            <BottomActionBar className="justify-between">
              <Button disabled={!warnWhen} type="submit">
                Submit
              </Button>
              <DeleteButton
                resource="performances"
                id={id?.toString()}
                onSuccess={() => list("performances")}
              />
            </BottomActionBar>
          </AutoForm>
        )}
      </div>
    </div>
  );
};
